import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import {
    colors,
    converteParaHorarioLocal,
    irradianciasMap,
    inversorChartOptions,
} from "../utils/utils";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-moment";
import moment from "moment";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    Legend
);

export default function GenericChart({
    selectLabel,
    randomColors,
    chartData,
    date,
    showIrradiation,
    irradiation,
}) {
    const baseOptions = {
        radius: 1,
        locale: "pt-br",
        responsive: true,
        plugins: {
            legend: {
                position: "top",
                align: "start"
            },
        },
        scales: {
            x: {
                grid: { display: false },
                type: "time",
                time: { unit: "hour", displayFormats: { hour: "HH:00" } },
                min: moment(new Date(date.year, date.month - 1, date.day, 5)),
                max: moment(new Date(date.year, date.month - 1, date.day, 20)),
            },
            secondXAxis: {
                axis: "x",
                labels: irradiation?.map((g) => converteParaHorarioLocal(g.data)),
                grid: { display: false },
                type: "time",
                display: false,
                min: moment(new Date(date.year, date.month - 1, date.day, 5)),
                max: moment(new Date(date.year, date.month - 1, date.day, 20)),
            },
            B: {
                type: "linear",
                position: "right",
                grid: { display: false },
                title: {
                    display: !!showIrradiation,
                    text: showIrradiation ? "Irradiância (W/m²)" : "",
                },
                min: -1,
            },
        },
        tension: 0.4,
    };

    const plottedData = { labels: [], datasets: [] };
    let colorIndex = 0;

    const addYAxisIfNeeded = (ylabels, equipament, variableName, label, color) => {
        const yAxisID = `${equipament}-${variableName}`;
        if (!ylabels[yAxisID]) {
            ylabels[yAxisID] = true;
            baseOptions.scales[yAxisID] = createYaxisConfig(label, color);
        }
        return yAxisID;
    };

    const createDataset = (key, label, yAxisID, color) => ({
        label,
        yAxisID,
        borderColor: color.borderColor,
        backgroundColor: color.backgroundColor,
        xAxisID: "x",
        fill: true,
        data: [],
    });

    for (const equipament in chartData) {
        const variableObject = chartData[equipament];
        const equipamentLabel = {};
        const ylabels = {};

        for (const variableName in variableObject) {
            const variableData = variableObject[variableName];
            const xAxisLabels = variableData?.map((d) => converteParaHorarioLocal(d?.date));
            const datasetsMap = {};

            variableData.forEach((variable) => {
                const valuesArray = Array.isArray(variable.value)
                    ? variable.value
                    : [variable];

                valuesArray.forEach((item) => {
                    const actualVariable = inversorChartOptions[equipament][variableName];
                    const actualEquipament = inversorChartOptions[equipament].label;
                    const equipmentCod = item.equipment_cod || "";
                    const variableLabel = item.label || "";

                    const yAxisID = addYAxisIfNeeded(
                        ylabels,
                        equipament,
                        variableName,
                        `${actualVariable} - ${actualEquipament}`,
                        randomColors[colorIndex]
                    );

                    const datasetKey = `${equipament}-${variableName}-${variableLabel || equipmentCod}`;
                    if (!equipamentLabel[datasetKey]) {
                        equipamentLabel[datasetKey] = true;
                        datasetsMap[datasetKey] = createDataset(
                            datasetKey,
                            `${actualVariable} ${equipmentCod ? variableLabel : ""} - ${actualEquipament} ${equipmentCod ? equipmentCod : variableLabel}`,
                            yAxisID,
                            randomColors[colorIndex]
                        );
                        colorIndex++;
                    }

                    datasetsMap[datasetKey].data.push(item?.value);
                });
            });

            Object.values(datasetsMap).forEach((ds) =>
                plottedData.datasets.push(ds)
            );

            xAxisLabels?.forEach((label) => {
                if (!plottedData.labels.includes(label)) plottedData.labels.push(label);
            });
        }
    }

    if (showIrradiation) {
        irradiation[0]?.estacaoDnis?.forEach((_, index) => {
            plottedData.datasets.push({
                label: `Irradiância ${irradianciasMap[index + 1]}`,
                yAxisID: "B",
                xAxisID: "secondXAxis",
                fill: true,
                data: irradiation?.map(
                    (e) => e.estacaoDnis.find((it) => it.cod == index + 1)?.valor
                ),
                borderColor: colors[index].borderColor,
                backgroundColor: colors[index].backgroundColor,
                pointRadius: 1,
            });
        });
    }

    return <Line options={baseOptions} data={plottedData} />;
}

function createYaxisConfig(labelName, color) {
    return {
        type: "linear",
        position: "left",
        title: { color: color.borderColor, display: true, text: labelName },
        ticks: { color: color.borderColor },
    };
}
